import React from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Button, Chip, Divider, Typography } from '@mui/material';
import { PageWrapper } from '../../../styledComponents/wrappers';

export const MOCK_SHOWCASE_ROUTE = '/fulfillment/mock';

interface LinkRow {
  label: string;
  description: string;
  path: string;
  chip?: string;
}

const PACK_LINKS: LinkRow[] = [
  { label: 'Pack Queue', description: 'All orders ready to pack', path: '/fulfillment/mock/pack' },
  {
    label: 'Single Bike — Box Planning (Step A)',
    description: '#TPC-8006 · Trek Madone SL5, auto-starts with Bike Box',
    path: '/fulfillment/mock/pack/8006',
    chip: 'bike',
  },
  {
    label: 'Single Bike — Scan Items (Step B)',
    description: '#TPC-8006 · Bike Box pre-added, scan bike to confirm',
    path: '/fulfillment/mock/pack/8006?step=1',
    chip: 'bike',
  },
  {
    label: 'Single Bike — Packing Slips (Step C)',
    description: '#TPC-8006 · bike packed, print slip',
    path: '/fulfillment/mock/pack/8006?step=2',
    chip: 'bike',
  },
  {
    label: 'Single Bike — Labels & Ship (Step D)',
    description: '#TPC-8006 · generate Shippo label and ship',
    path: '/fulfillment/mock/pack/8006?step=3',
    chip: 'bike',
  },
  {
    label: 'Bike + Accessories — Box Planning (Step A)',
    description: '#TPC-8007 · 2-box split, bike pre-assigned, accessories unassigned',
    path: '/fulfillment/mock/pack/8007',
    chip: '2-box',
  },
  {
    label: 'Bike + Accessories — Scan Items (Step B)',
    description: '#TPC-8007 · Domane SL6 in Box 1, pedals + tool kit need Box 2',
    path: '/fulfillment/mock/pack/8007?step=1',
    chip: '2-box',
  },
  {
    label: 'Bike + Accessories — Packing Slips (Step C)',
    description: '#TPC-8007 · print slips for both boxes',
    path: '/fulfillment/mock/pack/8007?step=2',
    chip: '2-box',
  },
  {
    label: 'Bike + Accessories — Labels & Ship (Step D)',
    description: '#TPC-8007 · two labels, Shippo rates per box',
    path: '/fulfillment/mock/pack/8007?step=3',
    chip: '2-box',
  },
  {
    label: 'PA&A Order — Box Planning (Step A)',
    description: '#TPC-8001 · single accessory item',
    path: '/fulfillment/mock/pack/8001',
  },
  {
    label: 'Mid-order — Scan Items (Step B)',
    description: '#TPC-8003 · 4 items, 2 boxes declared, 2 already scanned',
    path: '/fulfillment/mock/pack/8003?step=1',
    chip: 'mid-packing',
  },
  {
    label: 'Packing Slips (Step C)',
    description: '#TPC-8002 · 3-item priority order, all items packed',
    path: '/fulfillment/mock/pack/8002?step=2',
  },
  {
    label: 'Labels & Ship (Step D)',
    description: '#TPC-8002 · slips printed, Shippo rates in service select',
    path: '/fulfillment/mock/pack/8002?step=3',
  },
  {
    label: 'Labels — Address Error',
    description: '#TPC-8004 · address validation fails on first ship attempt',
    path: '/fulfillment/mock/pack/8004?step=3',
    chip: 'error state',
  },
  {
    label: 'Scan Step — Exception Item',
    description: '#TPC-8005 · one item flagged item_not_found',
    path: '/fulfillment/mock/pack/8005?step=1',
    chip: 'exception',
  },
];

const LPU_LINKS: LinkRow[] = [
  {
    label: 'LPU Queue',
    description: 'Ready to stage + awaiting pickup — two-section view',
    path: '/fulfillment/mock/lpu',
  },
  {
    label: 'Stage — Single Bike',
    description: '#TPC-9001 · Trek Madone, scan to confirm then move to LPU buffer',
    path: '/fulfillment/mock/lpu/9001/staging',
    chip: 'bike',
  },
  {
    label: 'Stage — Mid-confirmation',
    description: '#TPC-9003 · 1 of 2 items already confirmed (deep link demo)',
    path: '/fulfillment/mock/lpu/9003/staging',
    chip: 'mid-staging',
  },
  {
    label: 'Stage — Shopify Error on First Attempt',
    description: '#TPC-9002 · Giro helmet, first "Move to LPU Buffer" fails then succeeds',
    path: '/fulfillment/mock/lpu/9002/staging',
    chip: 'error state',
  },
  {
    label: 'Pickup — Awaiting (2 days)',
    description: '#TPC-9004 · Trek Domane, staged 2026-04-18, scan to confirm handoff',
    path: '/fulfillment/mock/lpu/9004/pickup',
  },
  {
    label: 'Pickup — Overdue (9 days, red warning)',
    description: '#TPC-9005 · Fizik saddle, staged 2026-04-11, over 7-day threshold',
    path: '/fulfillment/mock/lpu/9005/pickup',
    chip: 'overdue',
  },
  {
    label: 'Pickup — With Exception',
    description: '#TPC-9006 · one item damaged, confirm partial handoff',
    path: '/fulfillment/mock/lpu/9006/pickup',
    chip: 'exception',
  },
];

interface SectionProps {
  title: string;
  links: LinkRow[];
  onNavigate: (path: string) => void;
}

const LinkSection: React.FC<SectionProps> = ({ title, links, onNavigate }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
      {title}
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {links.map(link => (
        <Box
          key={link.path}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 1.5,
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 1,
            '&:hover': { borderColor: 'primary.main', bgcolor: '#f5f9ff' },
            cursor: 'pointer',
          }}
          onClick={() => onNavigate(link.path)}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{link.label}</Typography>
              {link.chip && <Chip label={link.chip} size="small" variant="outlined" />}
            </Box>
            <Typography sx={{ fontSize: 12, color: 'grey.600' }}>{link.description}</Typography>
          </Box>
          <Typography sx={{ fontSize: 12, color: 'grey.400' }}>→</Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const MockShowcase: React.FC = () => {
  const history = useHistory();

  return (
    <PageWrapper>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Typography variant="h4">Fulfillment v0.5 Mock Screens</Typography>
          <Chip label="MOCK" size="small" color="warning" />
        </Box>
        <Typography sx={{ fontSize: 14, color: 'grey.600' }}>
          Shareable testing hub for warehouse packers. All data is stubbed — no backend required.
          Share this URL with ops for hands-on feedback.
        </Typography>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <LinkSection title="Pack Flow" links={PACK_LINKS} onNavigate={p => history.push(p)} />

      <Divider sx={{ mb: 4 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          SKU Scan Entry
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 1.5,
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 1,
            '&:hover': { borderColor: 'primary.main', bgcolor: '#f5f9ff' },
            cursor: 'pointer',
          }}
          onClick={() => history.push('/fulfillment/mock/scan')}
        >
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Scan Entry</Typography>
            <Typography sx={{ fontSize: 12, color: 'grey.600' }}>
              Scan a SKU / UPC to find picked orders. Single-item orders flow through quick-ship
              without the full wizard.
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 12, color: 'grey.400' }}>→</Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <LinkSection title="Local Pickup (LPU)" links={LPU_LINKS} onNavigate={p => history.push(p)} />

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Button size="small" variant="outlined" onClick={() => history.push('/fulfillment/orders')}>
          Fulfillment Orders (real)
        </Button>
      </Box>
    </PageWrapper>
  );
};

export default MockShowcase;
